import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { bookingsAPI, servicesAPI, productsAPI, subscriptionsAPI } from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const ConfirmModal = ({ open, title, message, confirmLabel, confirmColor, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">Cancel</button>
          <button onClick={onConfirm} className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition ${confirmColor || 'bg-red-600 hover:bg-red-700'}`}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user, setRole } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirm, setConfirm] = useState({ open: false });
  const [toast, setToast] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [subInfo, setSubInfo] = useState(null);

  const isProvider = user?.role === 'provider' || user?.role === 'admin';

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          bookingsAPI.getMy({ limit: 10 }),
        ];
        if (isProvider) {
          promises.push(bookingsAPI.getProviderStats().catch(() => ({ data: { stats: null } })));
          promises.push(servicesAPI.getMy().catch(() => ({ data: { services: [] } })));
          promises.push(productsAPI.getMy().catch(() => ({ data: { products: [] } })));
        }
        const results = await Promise.all(promises);
        setBookings(results[0].data.bookings || []);
        if (isProvider) {
          setStats(results[1]?.data?.stats);
          setServices(results[2]?.data?.services || []);
          setProducts(results[3]?.data?.products || []);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Fetch subscription info for providers
    if (isProvider) {
      subscriptionsAPI.getMy().then(res => setSubInfo(res.data)).catch(() => {});
    }
  }, [isProvider]);

  const handleUpgradeToProvider = async () => {
    setUpgrading(true);
    try {
      const res = await setRole({ role: 'provider' });
      if (res.token) localStorage.setItem('token', res.token);
      showToast('Account upgraded to Provider! You can now post services and products.');
      window.location.reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to upgrade', 'error');
    } finally {
      setUpgrading(false);
    }
  };

  const handleToggleService = async (id) => {
    setActionLoading(`svc-toggle-${id}`);
    try {
      const res = await servicesAPI.toggleActive(id);
      setServices(prev => prev.map(s => s.id === id ? { ...s, is_active: res.data.service.is_active } : s));
      showToast(res.data.service.is_active ? 'Service is now visible' : 'Service is now hidden');
    } catch { showToast('Failed to update', 'error'); }
    setActionLoading(null);
  };

  const handleDeleteService = (id, title) => {
    setConfirm({
      open: true, title: 'Delete Service',
      message: `Are you sure you want to permanently delete "${title}"? This cannot be undone.`,
      confirmLabel: 'Delete', confirmColor: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirm({ open: false });
        setActionLoading(`svc-del-${id}`);
        try {
          await servicesAPI.delete(id);
          setServices(prev => prev.filter(s => s.id !== id));
          showToast('Service deleted');
        } catch { showToast('Failed to delete', 'error'); }
        setActionLoading(null);
      }
    });
  };

  const handleToggleProduct = async (id) => {
    setActionLoading(`prod-toggle-${id}`);
    try {
      const res = await productsAPI.toggleActive(id);
      setProducts(prev => prev.map(p => p.id === id ? { ...p, is_active: res.data.product.is_active } : p));
      showToast(res.data.product.is_active ? 'Product is now visible' : 'Product is now hidden');
    } catch { showToast('Failed to update', 'error'); }
    setActionLoading(null);
  };

  const handleDeleteProduct = (id, name) => {
    setConfirm({
      open: true, title: 'Delete Product',
      message: `Are you sure you want to permanently delete "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete', confirmColor: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirm({ open: false });
        setActionLoading(`prod-del-${id}`);
        try {
          await productsAPI.delete(id);
          setProducts(prev => prev.filter(p => p.id !== id));
          showToast('Product deleted');
        } catch { showToast('Failed to delete', 'error'); }
        setActionLoading(null);
      }
    });
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    accepted: 'bg-primary-100 text-primary-700 border-primary-200',
    in_progress: 'bg-purple-100 text-purple-700 border-purple-200',
    completed: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200',
  };

  const getImageUrl = (item) => {
    const images = typeof item.images === 'string' ? JSON.parse(item.images || '[]') : (item.images || []);
    return images.length > 0 ? `${API_URL}${images[0]}` : null;
  };

  if (loading) return <LoadingSpinner />;

  const activeServices = services.filter(s => s.is_active);
  const hiddenServices = services.filter(s => !s.is_active);
  const activeProducts = products.filter(p => p.is_active !== false);
  const hiddenProducts = products.filter(p => p.is_active === false);

  const tabs = isProvider ? [
    { id: 'overview', label: 'Overview', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { id: 'services', label: `Services (${services.length})`, icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    { id: 'products', label: `Products (${products.length})`, icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
    { id: 'bookings', label: `Bookings (${bookings.length})`, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  ] : [
    { id: 'overview', label: 'Overview', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { id: 'bookings', label: `My Bookings (${bookings.length})`, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  ];

  const renderItemCard = (item, type) => {
    const isService = type === 'service';
    const title = isService ? item.title : item.product_name;
    const img = getImageUrl(item);
    const isActive = item.is_active !== false;
    const toggleKey = isService ? `svc-toggle-${item.id}` : `prod-toggle-${item.id}`;
    const delKey = isService ? `svc-del-${item.id}` : `prod-del-${item.id}`;
    const isToggling = actionLoading === toggleKey;
    const isDeleting = actionLoading === delKey;
    const accent = isService ? 'primary' : 'green';
    const rating = parseFloat(item.avg_rating) || 0;
    const reviews = parseInt(item.total_reviews) || 0;

    return (
      <div key={item.id} className={`bg-white rounded-2xl border overflow-hidden group hover:shadow-lg transition-all duration-300 ${!isActive ? 'border-gray-200 opacity-75' : 'border-gray-100'}`}>
        <div className="relative h-44 bg-gray-100 overflow-hidden">
          {img ? (
            <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-200">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm ${isActive ? 'bg-green-500/90 text-white' : 'bg-gray-800/80 text-gray-300'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white animate-pulse' : 'bg-gray-400'}`}></span>
              {isActive ? 'Live' : 'Hidden'}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-lg font-medium capitalize">{type}</span>
          </div>
          {item.is_promoted && (
            <span className="absolute bottom-3 left-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-lg shadow-sm">⭐ PROMOTED</span>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-bold text-gray-900 text-sm leading-tight truncate mb-1">{title}</h3>
          <p className="text-gray-500 text-xs line-clamp-1 mb-2">{item.description}</p>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-${accent}-600 font-bold text-sm`}>{Number(item.price).toLocaleString()} RWF</span>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              {reviews > 0 && (
                <span className="flex items-center gap-0.5">
                  <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  {rating.toFixed(1)} ({reviews})
                </span>
              )}
              {!isService && <span>{item.stock} in stock</span>}
              {item.views > 0 && <span>{item.views} views</span>}
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
            {item.is_countrywide ? '🇷🇼 Countrywide' : (item.location || 'Rwanda')}
          </div>
          <div className="flex gap-2 pt-3 border-t border-gray-100">
            <Link to={`/${type}s/${item.id}`} className="flex-1 flex items-center justify-center gap-1 text-xs py-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 font-medium transition">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              View
            </Link>
            <Link to={`/${type}s/edit/${item.id}`} className={`flex-1 flex items-center justify-center gap-1 text-xs py-2 rounded-xl bg-${accent}-50 text-${accent}-700 hover:bg-${accent}-100 font-medium transition`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit
            </Link>
            <button onClick={() => isService ? handleToggleService(item.id) : handleToggleProduct(item.id)}
              disabled={isToggling}
              className={`flex-1 flex items-center justify-center gap-1 text-xs py-2 rounded-xl font-medium transition ${isActive ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'}`}>
              {isToggling ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
              ) : isActive ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              )}
              {isActive ? 'Hide' : 'Show'}
            </button>
            <button onClick={() => isService ? handleDeleteService(item.id, title) : handleDeleteProduct(item.id, title)}
              disabled={isDeleting}
              className="flex items-center justify-center gap-1 text-xs py-2 px-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 font-medium transition">
              {isDeleting ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ========================
  // CUSTOMER DASHBOARD
  // ========================
  const renderCustomerOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Recent Bookings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">My Bookings</h2>
            <Link to="/bookings" className="text-primary-600 text-sm font-medium hover:underline">View All →</Link>
          </div>
          {bookings.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-1">No bookings yet</h3>
              <p className="text-gray-500 text-sm mb-4">Browse services and book your first appointment</p>
              <Link to="/services" className="inline-flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-md transition">
                Browse Services
              </Link>
            </div>
          ) : (
            <div className="divide-y">
              {bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-4 hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate('/bookings')}>
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{b.service_title}</div>
                    <div className="text-gray-400 text-xs">{new Date(b.booking_date).toLocaleDateString()} • {b.booking_time}</div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[b.status]}`}>
                    {b.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Discover Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Discover on TopInfo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/services" className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 transition group">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 flex-shrink-0 group-hover:bg-primary-100 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0" /></svg>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-800 group-hover:text-primary-700">Browse Services</div>
                <div className="text-xs text-gray-500">Find professionals near you</div>
              </div>
            </Link>
            <Link to="/products" className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50/30 transition group">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-600 flex-shrink-0 group-hover:bg-green-100 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-800 group-hover:text-green-700">Shop Products</div>
                <div className="text-xs text-gray-500">Discover great deals</div>
              </div>
            </Link>
            <Link to="/favorites" className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-red-200 hover:bg-red-50/30 transition group">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 flex-shrink-0 group-hover:bg-red-100 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-800 group-hover:text-red-600">My Favorites</div>
                <div className="text-xs text-gray-500">Saved services & products</div>
              </div>
            </Link>
            <Link to="/messages" className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 transition group">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 flex-shrink-0 group-hover:bg-primary-100 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-800 group-hover:text-primary-700">Messages</div>
                <div className="text-xs text-gray-500">Chat with providers</div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Become Provider CTA */}
        <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-green-700 rounded-2xl shadow-lg p-6 text-white">
          <div className="mb-4">
            <svg className="w-10 h-10 text-white/80" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.15c0 .414.336.75.75.75z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold mb-1">Start Selling on TopInfo</h3>
          <p className="text-white/70 text-sm mb-4">Upgrade to a business account and start offering services or selling products today.</p>
          <button
            onClick={handleUpgradeToProvider}
            disabled={upgrading}
            className="w-full bg-white text-primary-700 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 transition shadow-md"
          >
            {upgrading ? 'Upgrading...' : 'Become a Provider'}
          </button>
        </div>

        {/* Account */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">My Account</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{user.first_name} {user.last_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">{user.phone || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{user.location || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Account type</span><span className="font-medium text-primary-600">Personal</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Member since</span><span className="font-medium">{new Date(user.created_at).toLocaleDateString()}</span></div>
          </div>
          <Link to="/profile" className="block text-center text-primary-600 text-sm font-medium mt-4 py-2 bg-primary-50 rounded-xl hover:bg-primary-100 transition">Edit Profile →</Link>
        </div>
      </div>
    </div>
  );

  // ========================
  // PROVIDER DASHBOARD
  // ========================
  const renderProviderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Recent Bookings */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Recent Bookings</h2>
            <Link to="/bookings" className="text-primary-600 text-sm font-medium hover:underline">View All →</Link>
          </div>
          {bookings.length === 0 ? (
            <div className="p-10 text-center">
              <div className="text-4xl mb-2">📅</div>
              <p className="text-gray-500 text-sm">No bookings yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-4 hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate('/bookings')}>
                  <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{b.service_title}</div>
                    <div className="text-gray-400 text-xs">{new Date(b.booking_date).toLocaleDateString()} • {b.booking_time}</div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[b.status]}`}>
                    {b.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Services Preview */}
        {services.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">My Services</h2>
              <button onClick={() => setActiveTab('services')} className="text-primary-600 text-sm font-medium hover:underline">Manage All →</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
              {services.slice(0, 4).map((s) => {
                const img = getImageUrl(s);
                return (
                  <Link key={s.id} to={`/services/${s.id}`} className="flex gap-3 p-2 rounded-xl hover:bg-gray-50 transition group">
                    <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300 text-xl">🔧</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-gray-800 truncate group-hover:text-primary-600">{s.title}</div>
                      <div className="text-primary-600 text-sm font-semibold">{Number(s.price).toLocaleString()} RWF</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${s.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        <span className="text-gray-400 text-xs">{s.is_active ? 'Live' : 'Hidden'}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Provider Sidebar */}
      <div className="space-y-6">
        {/* Subscription Plan Widget */}
        {subInfo && (() => {
          const sub = subInfo.subscription || {};
          const planIcons = { free: '🆓', basic: '⭐', premium: '💎' };
          const maxSvc = sub.max_services || 3;
          const maxPrd = sub.max_products || 5;
          const svcUsed = subInfo.serviceCount || 0;
          const prdUsed = subInfo.productCount || 0;
          const svcPct = maxSvc >= 999 ? 5 : Math.min((svcUsed / maxSvc) * 100, 100);
          const prdPct = maxPrd >= 999 ? 5 : Math.min((prdUsed / maxPrd) * 100, 100);
          const isFree = sub.plan === 'free';
          return (
            <div className={`rounded-2xl shadow-sm border p-5 ${isFree ? 'bg-gradient-to-br from-gray-50 to-white border-gray-200' : 'bg-gradient-to-br from-primary-50 to-white border-primary-100'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">Your Plan</h3>
                <span className="text-lg">{planIcons[sub.plan] || '🆓'}</span>
              </div>
              <p className="text-xl font-bold capitalize mb-3">{sub.plan} Plan</p>
              <div className="space-y-2.5 mb-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Services</span>
                    <span className="font-medium">{svcUsed}/{maxSvc >= 999 ? '∞' : maxSvc}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${svcPct >= 90 ? 'bg-red-500' : 'bg-primary-500'}`} style={{ width: `${svcPct}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Products</span>
                    <span className="font-medium">{prdUsed}/{maxPrd >= 999 ? '∞' : maxPrd}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${prdPct >= 90 ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${prdPct}%` }} />
                  </div>
                </div>
              </div>
              {sub.expires_at && (
                <p className="text-xs text-gray-400 mb-3">Expires: {new Date(sub.expires_at).toLocaleDateString()}</p>
              )}
              {isFree ? (
                <Link to="/subscription" className="block text-center bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-primary-700 transition">
                  Upgrade Plan →
                </Link>
              ) : (
                <Link to="/subscription" className="block text-center text-primary-600 text-sm font-medium py-2 bg-primary-50 rounded-xl hover:bg-primary-100 transition">
                  Manage Plan
                </Link>
              )}
            </div>
          );
        })()}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            {[
              { to: '/services/create', icon: '🔧', label: 'Add Service', bg: 'bg-primary-50 hover:bg-primary-100', text: 'text-primary-700' },
              { to: '/products/create', icon: '📦', label: 'Add Product', bg: 'bg-green-50 hover:bg-green-100', text: 'text-green-700' },
              { to: '/messages', icon: '💬', label: 'Messages', bg: 'bg-primary-50 hover:bg-primary-100', text: 'text-primary-700' },
              { to: '/profile', icon: '⚙️', label: 'Settings', bg: 'bg-gray-50 hover:bg-gray-100', text: 'text-gray-700' },
            ].map(a => (
              <Link key={a.label} to={a.to} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl ${a.bg} transition text-center`}>
                <span className="text-xl">{a.icon}</span>
                <span className={`text-xs font-medium ${a.text}`}>{a.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* My Products Preview */}
        {products.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">My Products</h3>
              <button onClick={() => setActiveTab('products')} className="text-green-600 text-sm font-medium hover:underline">Manage All →</button>
            </div>
            <div className="divide-y">
              {products.slice(0, 4).map((p) => {
                const img = getImageUrl(p);
                return (
                  <Link key={p.id} to={`/products/${p.id}`} className="flex items-center gap-3 p-3 hover:bg-gray-50 transition">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300">📦</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{p.product_name}</div>
                      <div className="text-green-600 text-xs font-semibold">{Number(p.price).toLocaleString()} RWF</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`w-1.5 h-1.5 inline-block rounded-full ${p.is_active !== false ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      <div className="text-xs text-gray-400">{p.stock} left</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Account Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Account</h3>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{user.first_name} {user.last_name}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">{user.phone || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{user.location || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Account type</span><span className="font-medium text-green-600">Business</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Member since</span><span className="font-medium">{new Date(user.created_at).toLocaleDateString()}</span></div>
          </div>
          <Link to="/profile" className="block text-center text-primary-600 text-sm font-medium mt-4 py-2 bg-primary-50 rounded-xl hover:bg-primary-100 transition">Edit Profile →</Link>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO title="Dashboard - TopInfo Rwanda" />
      <div className="min-h-screen bg-gray-50/50">
        {/* Toast */}
        {toast && (
          <div className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-slide-up ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
            {toast.type === 'error' ? '✕' : '✓'} {toast.msg}
          </div>
        )}

        <ConfirmModal {...confirm} onCancel={() => setConfirm({ open: false })} />

        {/* Dashboard Header */}
        <div className={`bg-gradient-to-br ${isProvider ? 'from-primary-600 via-primary-700 to-primary-800' : 'from-primary-600 via-primary-700 to-primary-800'} text-white`}>
          <div className="max-w-7xl mx-auto px-4 py-8 md:py-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-2xl font-bold shadow-lg">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold">Welcome back, {user.first_name}!</h1>
                <p className="text-primary-200 text-sm mt-1 flex items-center gap-2">
                  <span>{user.email}</span>
                  <span className="w-1 h-1 rounded-full bg-primary-300"></span>
                  <span>{user.location || 'Rwanda'}</span>
                  <span className="w-1 h-1 rounded-full bg-primary-300"></span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isProvider ? 'bg-green-500/30 text-green-200' : 'bg-white/20 text-white/80'}`}>
                    {isProvider ? '🏪 Business' : '👤 Personal'}
                  </span>
                </p>
              </div>
              {isProvider && (
                <div className="flex gap-2">
                  <Link to="/services/create" className="inline-flex items-center gap-1.5 bg-white text-primary-700 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-100 shadow-md transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                    Service
                  </Link>
                  <Link to="/products/create" className="inline-flex items-center gap-1.5 bg-white/20 text-white border border-white/30 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-white/30 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                    Product
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Bar - Only for providers */}
        {isProvider && (
          <div className="bg-white border-b border-gray-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-gray-100">
                {[
                  { value: services.length, label: 'Services', color: 'text-primary-600' },
                  { value: products.length, label: 'Products', color: 'text-green-600' },
                  { value: stats?.total || 0, label: 'Bookings', color: 'text-primary-600' },
                  { value: stats?.pending || 0, label: 'Pending', color: 'text-yellow-600' },
                  { value: hiddenServices.length + hiddenProducts.length, label: 'Hidden', color: 'text-gray-500' },
                  { value: Number(stats?.total_revenue || 0).toLocaleString(), label: 'Revenue (RWF)', color: 'text-primary-600' },
                ].map((s, i) => (
                  <div key={i} className={`py-4 px-3 text-center ${i >= 3 ? 'hidden md:block' : ''}`}>
                    <div className={`text-xl md:text-2xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Customer Stats Bar */}
        {!isProvider && (
          <div className="bg-white border-b border-gray-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid grid-cols-3 divide-x divide-gray-100">
                {[
                  { value: bookings.length, label: 'Bookings', color: 'text-primary-600' },
                  { value: bookings.filter(b => b.status === 'pending').length, label: 'Pending', color: 'text-yellow-600' },
                  { value: bookings.filter(b => b.status === 'completed').length, label: 'Completed', color: 'text-green-600' },
                ].map((s, i) => (
                  <div key={i} className="py-4 px-3 text-center">
                    <div className={`text-xl md:text-2xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                    activeTab === tab.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} /></svg>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto px-4 py-6">

          {/* Overview */}
          {activeTab === 'overview' && (isProvider ? renderProviderOverview() : renderCustomerOverview())}

          {/* Services Tab (Provider only) */}
          {activeTab === 'services' && isProvider && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">My Services</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{activeServices.length} active · {hiddenServices.length} hidden</p>
                </div>
                <Link to="/services/create" className="inline-flex items-center gap-1.5 bg-primary-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-md shadow-primary-500/20 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                  Add Service
                </Link>
              </div>
              {services.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /></svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">No services yet</h3>
                  <p className="text-gray-500 text-sm mb-5 max-w-xs mx-auto">Start listing your services to reach clients across Rwanda</p>
                  <Link to="/services/create" className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-primary-700 shadow-md transition">
                    Create Your First Service
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {services.map((s) => renderItemCard(s, 'service'))}
                </div>
              )}
            </div>
          )}

          {/* Products Tab (Provider only) */}
          {activeTab === 'products' && isProvider && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">My Products</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{activeProducts.length} active · {hiddenProducts.length} hidden</p>
                </div>
                <Link to="/products/create" className="inline-flex items-center gap-1.5 bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 shadow-md shadow-green-500/20 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                  Add Product
                </Link>
              </div>
              {products.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">No products yet</h3>
                  <p className="text-gray-500 text-sm mb-5 max-w-xs mx-auto">List your products and start selling across Rwanda</p>
                  <Link to="/products/create" className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-green-700 shadow-md transition">
                    Add Your First Product
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {products.map((p) => renderItemCard(p, 'product'))}
                </div>
              )}
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">My Bookings</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{bookings.length} total bookings</p>
                </div>
                <Link to="/bookings" className="text-primary-600 text-sm font-medium hover:underline">Full Page →</Link>
              </div>
              {bookings.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">No bookings yet</h3>
                  <p className="text-gray-500 text-sm mb-4">{isProvider ? 'Bookings from your customers will appear here' : 'Browse services and book your first appointment'}</p>
                  {!isProvider && (
                    <Link to="/services" className="inline-flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-md transition">
                      Browse Services
                    </Link>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
                  {bookings.map((b) => (
                    <div key={b.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate('/bookings')}>
                      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 flex-shrink-0">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">{b.service_title}</div>
                        <div className="text-gray-400 text-sm">{new Date(b.booking_date).toLocaleDateString()} at {b.booking_time}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[b.status]}`}>
                          {b.status?.replace('_', ' ')}
                        </span>
                        {b.total_price && <div className="text-sm font-medium text-gray-700 mt-1">{Number(b.total_price).toLocaleString()} RWF</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
